import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import CurrencyTagsInput from '../../../components/form/currency-tags-input.jsx';
import EntityLink from '../../../shared/ui/entity-link/entity-link.jsx';
import FloatingAlerts from '../../../shared/ui/floating-alerts/floating-alerts.jsx';
import LoadingContainer from '../../../shared/ui/loading-container/loading-container.jsx';
import { getEnumOptionLabel } from '../../../shared/i18n/enum-labels.js';
import { getTransactionStatusBadgeClass } from './transaction-status-badge.js';

function TransactionsPageView({
  tabs,
  activeTab,
  rows,
  isLoading,
  isLoadingMore,
  hasMoreTransactions,
  filters,
  statusOptions,
  countryOptions,
  isFilterOpen,
  error,
  selectTab,
  reloadActiveTab,
  loadMoreTransactions,
  updateFilter,
  onCountriesFilterChange,
  applyFilters,
  clearFilters,
  setIsFilterOpen,
}) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const activeTabLabel = tabs.find((tab) => tab.key === activeTab)?.label || t('transactions.title');
  const statusFilter = String(filters?.status || '').trim();

  return (
    <div>
      <FloatingAlerts error={error} />
      <div className="app-page-header d-flex align-items-start justify-content-between gap-3">
        <div>
          <h2>{t('transactions.title')}</h2>
          <p>{t('transactions.subtitle')}</p>
        </div>
        <div className="d-flex align-items-center gap-2 flex-wrap justify-content-end">
          <button
            type="button"
            className="btn btn-outline-secondary d-flex align-items-center"
            onClick={() => setIsFilterOpen((prev) => !prev)}
          >
            <i className="ti ti-adjustments-horizontal me-1 fs-5"></i>
            {t('transactions.filters')}
          </button>
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={reloadActiveTab}
            disabled={isLoading}
          >
            {t('transactions.refresh')} {activeTabLabel}
          </button>
        </div>
      </div>

      <div className="widget-content searchable-container list">
        {isFilterOpen ? (
          <div className="card card-body">
            <div className="row g-2">
              <div className="col-xl-2 col-md-4">
                <select
                  name="status"
                  className="form-select"
                  value={filters.status}
                  onChange={updateFilter}
                >
                  <option value="">{t('transactions.allStatuses')}</option>
                  {statusOptions.map((status) => (
                    <option key={status} value={status}>
                      {getEnumOptionLabel(t, 'transactionStatus', status)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-xl-2 col-md-4">
                <input
                  type="search"
                  name="method"
                  className="form-control"
                  value={filters.method}
                  onChange={updateFilter}
                  placeholder={t('transactions.filterMethod')}
                />
              </div>
              <div className="col-xl-2 col-md-4">
                <input
                  type="search"
                  name="account"
                  className="form-control"
                  value={filters.account}
                  onChange={updateFilter}
                  placeholder={t('transactions.filterAccount')}
                />
              </div>
              <div className="col-xl-2 col-md-4">
                <input
                  type="search"
                  name="transactionNumber"
                  className="form-control"
                  value={filters.transactionNumber}
                  onChange={updateFilter}
                  placeholder={t('transactions.filterTransactionNumber')}
                />
              </div>
              <div className="col-xl-2 col-md-4">
                <input
                  type="search"
                  name="reference"
                  className="form-control"
                  value={filters.reference}
                  onChange={updateFilter}
                  placeholder={t('transactions.filterReference')}
                />
              </div>
              <div className="col-xl-3 col-md-6">
                <input
                  type="search"
                  name="profileName"
                  className="form-control"
                  value={filters.profileName}
                  onChange={updateFilter}
                  placeholder={t('transactions.filterProfileName')}
                />
              </div>
              <div className="col-xl-3 col-md-6">
                <input
                  type="search"
                  name="profileEmail"
                  className="form-control"
                  value={filters.profileEmail}
                  onChange={updateFilter}
                  placeholder={t('transactions.filterProfileEmail')}
                />
              </div>
              <div className="col-lg-8">
                <label className="form-label mb-1">{t('transactions.countries')}</label>
                <CurrencyTagsInput
                  value={filters.countriesOnly}
                  options={countryOptions}
                  onChange={onCountriesFilterChange}
                  placeholderText={t('transactions.selectCountries')}
                />
              </div>
              <div className="col-lg-4 d-flex align-items-end justify-content-end gap-2">
                <button type="button" className="btn btn-primary" onClick={applyFilters}>
                  {t('transactions.applyFilters')}
                </button>
                <button type="button" className="btn btn-outline-secondary" onClick={clearFilters}>
                  {t('transactions.clearFilters')}
                </button>
              </div>
            </div>
          </div>
        ) : null}
        <div className={`card card-body${isFilterOpen ? ' mt-3' : ''}`}>
          <div className="app-profile-details-tabs mb-3">
            <ul className="nav nav-tabs flex-nowrap">
              {tabs.map((tab) => (
                <li key={tab.key} className="nav-item">
                  <button
                    type="button"
                    className={`nav-link ${activeTab === tab.key ? 'active' : ''}`}
                    onClick={() => selectTab(tab.key)}
                  >
                    {tab.label}
                  </button>
                </li>
              ))}
            </ul>
            {statusFilter ? (
              <div className="mt-2">
                <span className="badge rounded-pill text-bg-light text-uppercase">
                  {t('common.status')}: {statusFilter}
                </span>
              </div>
            ) : null}
          </div>

          <LoadingContainer isLoading={isLoading} hasData={rows.length > 0}>
            <table className="table search-table align-middle text-nowrap mb-0">
              <thead className="header-item">
                <tr>
                  <th>{t('common.status')}</th>
                  <th>{t('transactions.profile')}</th>
                  <th>{t('transactions.amount')}</th>
                  <th>{t('transactions.method')}</th>
                  <th>{t('transactions.transactionNumber')}</th>
                  <th>{t('transactions.reference')}</th>
                  <th>{t('transactions.account')}</th>
                  <th>{t('transactions.assignedTo')}</th>
                  <th>{t('common.date')}</th>
                </tr>
              </thead>
              <tbody>
                {!isLoading && rows.length === 0 ? (
                  <tr>
                    <td colSpan="9" className="text-center text-muted">
                      {t('transactions.noTransactionsFound')}
                    </td>
                  </tr>
                ) : null}

                {rows.map((row) => (
                  <tr
                    key={row.key}
                    className={row.id ? 'app-clickable-row' : ''}
                    onClick={() => {
                      if (row.id) {
                        navigate(`/transactions/${encodeURIComponent(row.id)}`);
                      }
                    }}
                  >
                    <td>
                      <span className={`badge rounded-pill fw-semibold text-uppercase ${getTransactionStatusBadgeClass(row.status)}`}>
                        {row.status || '-'}
                      </span>
                    </td>
                    <td>
                      {row.profileId ? (
                        <EntityLink
                          to={`/profiles/${encodeURIComponent(row.profileId)}`}
                          onClick={(event) => event.stopPropagation()}
                        >
                          {row.profileName}
                        </EntityLink>
                      ) : (
                        row.profileName
                      )}
                    </td>
                    <td>
                      <span className="fw-semibold">{row.amountFormatted}</span>
                    </td>
                    <td>{row.methodId}</td>
                    <td>{row.transactionNumber}</td>
                    <td>{row.reference}</td>
                    <td>{row.accountLogin}</td>
                    <td>{row.assignedToName}</td>
                    <td>{row.createdAt}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </LoadingContainer>
          {hasMoreTransactions && !isLoading ? (
            <div className="text-center mt-3">
              <button
                type="button"
                className="btn btn-outline-primary"
                onClick={loadMoreTransactions}
                disabled={isLoadingMore}
              >
                {isLoadingMore ? t('common.loading') : t('transactions.loadMore')}
              </button>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default TransactionsPageView;
